import {Button} from '@components/ui/button/Button.tsx'
import {useTranslation} from 'react-i18next'
import {
    BriefcaseIcon,
    CalendarIcon,
    ChevronLeftIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    FileTextIcon,
    MapPinIcon,
    ShieldIcon,
    TagIcon
} from '@components/ui/icon/Icon.tsx'
import {
    StyledContainerInfo,
    StyledCounterPartName,
    StyledCounterPartRow,
    StyledMeasureDetailsContainer,
    StyledMeasureDetailsWrapper,
    StyledWarningIsOmissis,
    StyledExpandableContainer,
    StyledBlurOverlay,
    StyledExpandButton,
    StyledPMQTitle,
    StyledDivider
} from '@pages/measure-details/style.ts'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Info} from '@/features/measures/components/info/Info.tsx'
import {StyledLink} from '@pages/measures/style.ts'
import dayjs from '@/dayjs.ts'
import {MeasureType} from '@/features/measures/components/measure-type/MeasureType.tsx'
import {SUB_TYPES} from '@/features/measures/services/types.ts'
import {MeasureDetailsHeader} from '@/features/measure-details/components/measure-details-header/MeasureDetailsHeader.tsx'
import {useGetMeasureDetails} from '@/features/measure-details/services/useGetMeasureDetails.ts'
import {capitalize, formatGeneralRoleNumber} from '@utilities/helpers.ts'
import {Chip} from '@components/ui/chip/Chip.tsx'
import {useTheme} from 'styled-components'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {useState} from 'react'
import {useIsOverflowing} from '@/hooks/useIsOverflowing'

export const MeasureDetails = () => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const {state} = useLocation()
    const theme = useTheme()
    const params = useParams()
    const measureQuery = useGetMeasureDetails(params?.id)
    const measure = measureQuery?.data
    const [isExpanded, setIsExpanded] = useState(false)
    const [isPqmExpanded, setIsPqmExpanded] = useState(false)
    const {isOverflowing: isCounterpartOverflowing, containerRef: counterpartRef} = useIsOverflowing([
        measure?.involvedParties
    ])
    const {isOverflowing: isPqmOverflowing, containerRef: pqmRef} = useIsOverflowing([measure?.pqmSection])

    const handleBack = () => {
        if (!state) return navigate(routes.MEASURES.path)
        navigate(state.from)
    }

    const involvedParties = () => {
        if (!measure || (measure?.involvedParties?.length === 0 && !measure?.isOmissis)) return '-'
        if (measure?.involvedParties?.length === 0 && measure?.isOmissis)
            return (
                <StyledCounterPartName $isOmissis={measure?.isOmissis}>
                    {' '}
                    <Chip text={'omissis'} />
                </StyledCounterPartName>
            )

        return (
            <Flexbox direction="column" gap={2}>
                <StyledExpandableContainer $isExpanded={isExpanded} ref={counterpartRef}>
                    <Flexbox gap={2} align={'center'} style={{flexWrap: 'wrap'}}>
                        {measure?.involvedParties?.map(panel => <Chip key={panel.id} text={panel.name} />)}
                    </Flexbox>
                    {isCounterpartOverflowing && <StyledBlurOverlay $isExpanded={isExpanded} />}
                </StyledExpandableContainer>

                {isCounterpartOverflowing && (
                    <StyledExpandButton variant="primaryGhost" onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? (
                            <>
                                {t('measure_details:show_less')}
                                <ChevronUpIcon />
                            </>
                        ) : (
                            <>
                                {t('measure_details:show_more')}
                                <ChevronDownIcon />
                            </>
                        )}
                    </StyledExpandButton>
                )}
            </Flexbox>
        )
    }

    return (
        <StyledMeasureDetailsContainer gap={6} direction={'column'} fullWidth>
            <Button variant={'ghost'} onClick={handleBack}>
                <ChevronLeftIcon />
                {t('measures:measures')}
            </Button>
            {measureQuery.isLoading ? (
                <Flexbox justify={'center'} align={'center'} fullWidth>
                    <Spinner />
                </Flexbox>
            ) : (
                <StyledMeasureDetailsWrapper
                    direction={'column'}
                    justify={'center'}
                    align={'flex-start'}
                    fullWidth
                    gap={4}
                >
                    <MeasureDetailsHeader measure={measure} />

                    <StyledCounterPartRow>
                        <Flexbox direction={'column'} gap={2}>
                            <span>{t('measure_details:counter_part')}</span>

                            <Flexbox gap={2} align={'center'} style={{flexWrap: 'wrap'}}>
                                {involvedParties()}
                            </Flexbox>
                        </Flexbox>

                        <Flexbox direction={'column'} gap={2}>
                            <span>{t('measure_details:panel')}</span>
                            <Flexbox gap={2} align={'center'} style={{flexWrap: 'wrap'}}>
                                {measure?.judicialPanels?.length
                                    ? measure?.judicialPanels?.map(panel => (
                                          <Chip
                                              key={panel.id}
                                              text={panel.name}
                                              bgColor={theme.palette.secondary['25']}
                                              textColor={theme.palette.secondary['600']}
                                          />
                                      ))
                                    : '-'}
                            </Flexbox>
                        </Flexbox>
                    </StyledCounterPartRow>

                    <StyledContainerInfo>
                        <Info
                            title={t('measure_details:info:site')}
                            description={capitalize(measure?.headquarterLocation?.keyName.replace(/_/g, ' ') || '')}
                            icon={<MapPinIcon />}
                        />
                        <Info
                            title={t('measure_details:info:section')}
                            description={measure?.section?.keyName}
                            icon={<TagIcon />}
                        />
                        <Info
                            title={t('measure_details:info:publication_date')}
                            description={measure?.publishedAt ? dayjs(measure?.publishedAt).format('DD/MM/YYYY') : '-'}
                            icon={<CalendarIcon />}
                        />
                        <Info
                            title={t('measure_details:info:type_of_measure')}
                            description={<MeasureType type={measure?.subType as SUB_TYPES} />}
                            icon={<FileTextIcon />}
                        />
                        <Info
                            title={t('measure_details:info:measure_number')}
                            description={measure?.measureNumber}
                            icon={<ShieldIcon />}
                            canCopy
                        />
                        <Info
                            title={t('measure_details:info:general_role_number')}
                            description={
                                measure?.appealMeasureId ? (
                                    <StyledLink href={`/measure/${measure?.appealMeasureId}`} target={'_blank'}>
                                        {formatGeneralRoleNumber(measure?.appealNumber?.toString() || '')}
                                    </StyledLink>
                                ) : (
                                    formatGeneralRoleNumber(measure?.appealNumber?.toString() || '')
                                )
                            }
                            icon={<BriefcaseIcon />}
                            canCopy={!measure?.appealMeasureId}
                            copyText={formatGeneralRoleNumber(measure?.appealNumber?.toString() || '')}
                        />
                    </StyledContainerInfo>

                    <StyledDivider />

                    <Flexbox direction={'column'} gap={2}>
                        <StyledPMQTitle>{t('measure_details:pqm_section')} </StyledPMQTitle>
                        <StyledExpandableContainer $isExpanded={isPqmExpanded} ref={pqmRef}>
                            {measure?.pqmSection ? (
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: capitalize(measure?.pqmSection)
                                    }}
                                />
                            ) : (
                                t('measure_details:pmq_not_available')
                            )}
                            {!isPqmExpanded && isPqmOverflowing && <StyledBlurOverlay $isExpanded={false} />}
                        </StyledExpandableContainer>
                        {isPqmOverflowing && (
                            <StyledExpandButton variant="primaryGhost" onClick={() => setIsPqmExpanded(!isPqmExpanded)}>
                                {isPqmExpanded ? (
                                    <>
                                        {t('measure_details:show_less')}
                                        <ChevronUpIcon />
                                    </>
                                ) : (
                                    <>
                                        {t('measure_details:show_more')}
                                        <ChevronDownIcon />
                                    </>
                                )}
                            </StyledExpandButton>
                        )}
                    </Flexbox>

                    {measure?.isOmissis && (
                        <StyledWarningIsOmissis>* {t('measure_details:warning')}</StyledWarningIsOmissis>
                    )}
                </StyledMeasureDetailsWrapper>
            )}
        </StyledMeasureDetailsContainer>
    )
}
